import { Button } from "@/components/ui/button";

/**
 * Tracker Slots Panel — extra sub_ids attached to a publisher for use across
 * CPA networks (Impact / Partnerize / Skimlinks / mrge / Yieldkit). Each slot
 * becomes a distinct sub_id1 so a single publisher can run multiple URLs to
 * the same brand and have revenue attributed per URL. Shopnomix is excluded
 * — its revenue feed doesn't carry per-link sub_id.
 */
export function TrackerSlotsPanel({
  publisher,
  slots,
  slotsLoading,
  creatingSlot,
  onAddSlot,
  onToggleSlot,
}) {
  return (
    <div className="bg-white border border-slate-200 rounded-lg p-5 mb-6" data-testid="slots-panel">
      <div className="flex items-start justify-between mb-4">
        <div>
          <div className="text-[10px] font-bold tracking-[0.2em] uppercase text-slate-500 mb-1">Tracker Slots</div>
          <p className="text-xs text-slate-500">
            Extra sub_ids for CPA networks (Impact, Partnerize, Skimlinks, mrge, Yieldkit).
            Use to issue multiple URLs per brand with per-URL revenue attribution.
          </p>
        </div>
        <Button
          size="sm"
          onClick={onAddSlot}
          disabled={creatingSlot}
          className="bg-[#002FA7] hover:bg-[#001f6f] text-white h-8"
          data-testid="add-slot-btn"
        >
          {creatingSlot ? "Creating…" : "+ Add slot"}
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
        {/* Main sub_id row — read-only, cannot be disabled */}
        <div className="flex items-center justify-between border border-slate-100 rounded px-3 py-2 bg-slate-50/60">
          <div className="flex items-center gap-3">
            <span className="inline-flex w-7 h-7 items-center justify-center bg-[#002FA7] text-white rounded text-xs font-bold">·</span>
            <div>
              <div className="font-mono text-sm font-bold text-slate-900">{publisher.sub_id || "—"}</div>
              <div className="text-[10px] text-slate-400">Main</div>
            </div>
          </div>
          <span className="text-[10px] font-bold text-emerald-700 bg-emerald-50 border border-emerald-200 px-2 py-0.5 rounded uppercase tracking-wider">Active</span>
        </div>

        {slots.map((s) => {
          const isActive = (s.status || "active") === "active";
          return (
            <div
              key={s.sub_id}
              className={`flex items-center justify-between border rounded px-3 py-2 transition-colors ${isActive ? "border-slate-200 bg-white" : "border-slate-100 bg-slate-50/40"}`}
              data-testid={`slot-row-${s.sub_id}`}
            >
              <div className="flex items-center gap-3">
                <span className={`inline-flex w-7 h-7 items-center justify-center rounded text-xs font-bold ${isActive ? "bg-[#002FA7] text-white" : "bg-slate-200 text-slate-400"}`}>
                  {s.label || "·"}
                </span>
                <div>
                  <div className={`font-mono text-sm font-bold ${isActive ? "text-slate-900" : "text-slate-400 line-through"}`}>{s.sub_id}</div>
                  <div className="text-[10px] text-slate-400">{new Date(s.created_at).toLocaleDateString()}</div>
                </div>
              </div>
              <Button
                size="sm"
                variant="outline"
                onClick={() => onToggleSlot(s.sub_id)}
                className="h-7 text-[11px]"
                data-testid={`slot-toggle-${s.sub_id}`}
              >
                {isActive ? "Disable" : "Enable"}
              </Button>
            </div>
          );
        })}

        {!slotsLoading && slots.length === 0 && (
          <div className="col-span-full px-3 py-4 text-center text-xs text-slate-400">
            No extra slots yet. Click "+ Add slot" to mint one.
          </div>
        )}
      </div>
    </div>
  );
}
